import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  IconButton,
  Grid,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '@/services/api';
import { PlayerAvatar } from './PlayerAvatar';
import CryptoJS from 'crypto-js';

interface PlayerPhotoConfig {
  type: 'UPLOAD' | 'GRAVATAR';
  hasUploadedPhoto: boolean;
  email: string | null;
  urls: {
    small: string;
    medium: string;
    large: string;
  };
}

interface PlayerProfilePhotoManagerProps {
  playerId: string;
  playerEmail?: string;
  wizardMode?: boolean;
  onPhotoChange?: (file: File | null) => void;
  onPhotoTypeChange?: (type: 'UPLOAD' | 'GRAVATAR') => void;
  initialPhotoType?: 'UPLOAD' | 'GRAVATAR';
  onPhotoPreviewChange?: (url: string | null) => void;
  initialPhotoPreview?: string | null;
}

export const PlayerProfilePhotoManager: React.FC<PlayerProfilePhotoManagerProps> = ({ 
  playerId, 
  playerEmail,
  wizardMode = false,
  onPhotoChange = () => {},
  onPhotoTypeChange = () => {},
  initialPhotoType = 'UPLOAD',
  onPhotoPreviewChange = () => {},
  initialPhotoPreview = null
}) => {
  const [config, setConfig] = useState<PlayerPhotoConfig | null>(null);
  const [photoType, setPhotoType] = useState<'UPLOAD' | 'GRAVATAR'>(initialPhotoType);
  const [loading, setLoading] = useState(false);
  
  // Use the props to avoid TypeScript warnings
  void { wizardPhotoPreview: initialPhotoPreview, onPhotoChange, onPhotoTypeChange, onPhotoPreviewChange };

  // Generate Gravatar URL for preview
  const getGravatarUrl = (email: string, size: number = 120) => {
    const hash = CryptoJS.MD5(email.trim().toLowerCase()).toString();
    return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
  };

  useEffect(() => {
    if (!wizardMode) {
      loadConfig();
      
      // Listen for config changes
      const handleConfigChange = () => {
        loadConfig();
      };
      
      window.addEventListener('playerPhotoConfigChanged', handleConfigChange);
      return () => {
        window.removeEventListener('playerPhotoConfigChanged', handleConfigChange);
      };
    } else {
      // In wizard mode, set up default config without API call
      setConfig({
        type: initialPhotoType,
        hasUploadedPhoto: false,
        email: playerEmail || null,
        urls: {
          small: '',
          medium: '',
          large: ''
        }
      });
      setPhotoType(initialPhotoType);
    }
  }, [wizardMode, playerEmail, initialPhotoType]); // Removed playerId dependency

  // Separate effect for playerId changes in non-wizard mode
  useEffect(() => {
    if (!wizardMode && playerId && playerId !== 'wizard-temp') {
      loadConfig();
    }
  }, [playerId, wizardMode]);

  useEffect(() => {
    if (config) {
      setPhotoType(config.type);
    }
  }, [config]);

  const loadConfig = async () => {
    // Don't load config in wizard mode or for wizard-temp playerId
    if (wizardMode || playerId === 'wizard-temp') return;
    
    try {
      const response = await api.get(
        `/players/${playerId}/profile-photo/config`
      );
      setConfig(response.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load photo configuration');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (wizardMode) {
      // In wizard mode, just store the file and create preview
      const previewUrl = URL.createObjectURL(file);
      onPhotoChange(file);
      onPhotoPreviewChange(previewUrl);
      toast.success('Photo selected for upload');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.post(
        `/players/${playerId}/profile-photo/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setConfig(response.data.config);
      toast.success('Profile photo uploaded successfully');
      
      // Notify other components that photo was updated
      window.dispatchEvent(new Event('playerPhotoUpdated'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoTypeChange = async (newType: 'UPLOAD' | 'GRAVATAR') => {
    setPhotoType(newType);
    
    if (newType === 'GRAVATAR') {
      // Use the player's email from props
      if (!playerEmail?.trim()) {
        toast.error('Please set an email address in the player profile first');
        setPhotoType(config?.type || 'UPLOAD');
        return;
      }

      if (wizardMode) {
        // In wizard mode, just update the config locally
        setConfig(prev => prev ? {
          ...prev,
          type: 'GRAVATAR',
          email: playerEmail.trim()
        } : null);
        onPhotoTypeChange(newType);
        // Don't clear the upload preview - keep it for when user switches back
        toast.success('Gravatar will be used when player is created');
        return;
      }
      
      setLoading(true);

      try {
        const response = await api.post(
          `/players/${playerId}/profile-photo/gravatar`,
          { email: playerEmail.trim() }
        );
        setConfig(response.data.config);
        toast.success('Switched to Gravatar successfully');
        window.dispatchEvent(new Event('playerPhotoUpdated'));
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to switch to Gravatar');
        setPhotoType(config?.type || 'UPLOAD');
      } finally {
        setLoading(false);
      }
    } else {
      // Switching to UPLOAD mode
      if (wizardMode) {
        // In wizard mode, just update the config locally
        setConfig(prev => prev ? {
          ...prev,
          type: 'UPLOAD'
        } : null);
        onPhotoTypeChange(newType);
        return;
      }

      setLoading(true);

      try {
        if (!config?.hasUploadedPhoto) {
          toast.error('Please upload a photo first');
          setPhotoType('GRAVATAR');
          return;
        }
        const response = await api.post(
          `/players/${playerId}/profile-photo/upload-mode`,
          {}
        );
        setConfig(response.data.config);
        toast.success('Switched to uploaded photo successfully');
        window.dispatchEvent(new Event('playerPhotoUpdated'));
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to switch photo type');
        setPhotoType(config?.type || 'UPLOAD');
      } finally {
        setLoading(false);
      }
    }
  };



  if (!config) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} sm="auto">
          <Box 
            position="relative" 
            display="flex" 
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
            onClick={() => {
              if (photoType === 'UPLOAD') {
                const fileInput = document.getElementById('player-photo-upload-input') as HTMLInputElement;
                fileInput?.click();
              }
            }}
            sx={{ 
              cursor: photoType === 'UPLOAD' ? 'pointer' : 'default',
              '&:hover': photoType === 'UPLOAD' ? {
                opacity: 0.8,
                transition: 'opacity 0.2s'
              } : {}
            }}
          >
            <Box position="relative">
              {wizardMode && photoType === 'UPLOAD' && initialPhotoPreview ? (
                <Box
                  component="img"
                  src={initialPhotoPreview}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
              ) : wizardMode && photoType === 'GRAVATAR' && playerEmail?.trim() ? (
                <Box
                  component="img"
                  src={getGravatarUrl(playerEmail)}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
              ) : (
                <PlayerAvatar
                  playerId={playerId}
                  size="large"
                />
              )}
              {photoType === 'UPLOAD' && (
                <>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="player-photo-upload-input"
                    type="file"
                    onChange={handleFileSelect}
                    disabled={loading}
                  />
                  <label htmlFor="player-photo-upload-input">
                    <IconButton
                      component="span"
                      disabled={loading}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                      }}
                      size="small"
                    >
                      <PhotoCamera fontSize="small" />
                    </IconButton>
                  </label>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm>
          <Typography variant="subtitle2" gutterBottom>
            Photo Source
          </Typography>
          <RadioGroup
            value={photoType}
            onChange={(e) => handlePhotoTypeChange(e.target.value as 'UPLOAD' | 'GRAVATAR')}
          >
            <FormControlLabel
              value="UPLOAD"
              control={<Radio />}
              label="Upload custom photo"
              disabled={loading}
            />
            <FormControlLabel
              value="GRAVATAR"
              control={<Radio />}
              label="Use Gravatar"
              disabled={loading}
            />
          </RadioGroup>

          {photoType === 'GRAVATAR' && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Using Gravatar with email from player profile
            </Typography>
          )}

          {photoType === 'UPLOAD' && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Supported formats: JPEG, PNG, WebP. Maximum size: 5MB
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
